import React, { useState, useMemo } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ChevronDown, Plus, Search as SearchIcon, X } from "lucide-react";

/**
 * Searchable select with optional "Add New" CTA.
 * Suitable for long lists (e.g. thousands of cities).
 * options: [{id, label}]
 */
export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  onAddNew,
  testId = "searchable-select",
  disabled,
  emptyMessage = "No options",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = options.find((o) => o.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 200);
    return options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 200);
  }, [options, query]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className="w-full h-10 border vsm-border vsm-surface px-3 text-sm flex items-center gap-2 rounded-none text-left disabled:opacity-50"
        >
          <span className={selected ? "" : "vsm-muted"}>
            {selected ? selected.label : placeholder}
          </span>
          {selected && !disabled && (
            <span
              role="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }}
              className="ml-auto p-0.5 vsm-muted hover:opacity-100"
              data-testid={`${testId}-clear`}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 opacity-60 ${selected && !disabled ? "" : "ml-auto"}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="rounded-none p-0 w-[var(--radix-popover-trigger-width)] border vsm-border vsm-surface"
        data-testid={`${testId}-popover`}
      >
        <div className="border-b vsm-border p-2 relative">
          <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 vsm-muted" />
          <Input
            autoFocus
            data-testid={`${testId}-search-input`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="rounded-none pl-8 h-9"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-sm vsm-muted">{emptyMessage}</div>
          )}
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              data-testid={`${testId}-option-${o.id}`}
              onClick={() => {
                onChange(o.id);
                setOpen(false);
                setQuery("");
              }}
              className={`w-full px-3 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 text-left ${
                value === o.id ? "vsm-primary-text font-semibold" : ""
              }`}
            >
              {o.label}
            </button>
          ))}
          {query && options.length > filtered.length && (
            <div className="px-3 py-2 text-xs vsm-muted border-t vsm-border">
              Showing {filtered.length} of {options.length}. Refine your search.
            </div>
          )}
        </div>
        {onAddNew && (
          <div className="border-t vsm-border">
            <button
              type="button"
              data-testid={`${testId}-add-new`}
              onClick={() => {
                setOpen(false);
                setQuery("");
                onAddNew();
              }}
              className="w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 font-medium"
            >
              <Plus className="w-4 h-4" /> Add new
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
